import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { Popover } from "antd";
import { DownOutlined, NotificationOutlined } from "@ant-design/icons";
import { LegendCircle } from "../../visuals/styledComponents";
import { useCompanyNews } from "../../../hooks/useCompanyNews";
import { useNewsSeenAt } from "../../../hooks/useNewsSeenAt";
import { useNewsPreparation } from "../../../hooks/useNewsPreparation";
import { useNewsSearches } from "../../../hooks/useNewsSearches";
import {
  compassLightGreen,
  darkGreen,
  greyText,
  lightGreyText,
} from "../../../styles";
import { MESSAGES } from "../../../messages";
import { NewsDayBars } from "./NewsDayBars";
import { NewsTimelineCard } from "./NewsTimelineCard";
import { NEWS_LIMIT, NEWS_WINDOW_DAYS } from "./constants";
import {
  isNewSince,
  isSameNewsRange,
  newsWindowLabel,
  trailingNewsRange,
} from "./utils";
import { PublishedRange } from "../../../models/newsModels";

type NewsStatusSegmentProps = {
  companyId: string;
};

const SegmentButton = styled.button<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-left: 12px;
  padding: 6px 10px;
  border: 1px solid
    ${({ $open }): string => ($open ? compassLightGreen : "transparent")};
  border-radius: 8px;
  background: none;
  font-family: "Apercu";
  text-align: left;
  cursor: pointer;
  &:focus-visible {
    outline: 1px solid ${compassLightGreen};
  }
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${greyText};
`;

const ValueRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: ${greyText};
`;

const Count = styled.span`
  color: ${darkGreen};
`;

const NewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${darkGreen};
`;

const Chevron = styled(DownOutlined)`
  font-size: 9px;
  color: ${lightGreyText};
`;

const PopoverBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 480px;
  max-height: 560px;
  padding: 6px 4px;
`;

const TINY_BAR_WIDTH = 3;
const TINY_BAR_HEIGHT = 18;

export const NewsStatusSegment = ({
  companyId,
}: NewsStatusSegmentProps): JSX.Element => {
  const companyScope = useMemo(() => [companyId], [companyId]);
  const [publishedRange, setPublishedRange] = useState<PublishedRange>(() =>
    trailingNewsRange(NEWS_WINDOW_DAYS),
  );
  const { news, isLoading, reload } = useCompanyNews({
    compassCids: companyScope,
    portfolioIndexIds: null,
    days: NEWS_WINDOW_DAYS,
    publishedRange,
    limit: NEWS_LIMIT,
  });
  const { searches, reload: reloadSearches } = useNewsSearches(companyScope);
  const showSearchedRange = useCallback(
    (searchedRange: PublishedRange | null): void => {
      if (searchedRange && !isSameNewsRange(searchedRange, publishedRange)) {
        setPublishedRange(searchedRange);
        return;
      }
      reload();
    },
    [publishedRange, reload],
  );
  const { prepare, progress } = useNewsPreparation({
    compassCids: companyScope,
    portfolioIndexIds: null,
    publishedRange,
    onResults: showSearchedRange,
    onSearched: reloadSearches,
  });
  const windowLabel = newsWindowLabel(publishedRange);
  const { seenAt, markSeen } = useNewsSeenAt(companyId);
  const [open, setOpen] = useState(false);
  const newCount = useMemo(
    () =>
      news.items.filter((item) => isNewSince(item.published_at, seenAt)).length,
    [news.items, seenAt],
  );

  const handleOpenChange = (nextOpen: boolean): void => {
    if (nextOpen) {
      markSeen();
    }
    setOpen(nextOpen);
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      destroyTooltipOnHide={true}
      content={
        <PopoverBody>
          <NewsTimelineCard
            news={news}
            isLoading={isLoading}
            refreshControl={{
              refreshLabel: MESSAGES.NEWS_FEED.REFRESH_COMPANY,
              generateLabel: MESSAGES.NEWS_FEED.GENERATE_COMPANY,
              onRefresh: prepare,
              progress,
            }}
            rangeControl={{
              value: publishedRange,
              onChange: setPublishedRange,
            }}
            searches={searches}
          />
        </PopoverBody>
      }
    >
      <SegmentButton type="button" $open={open} aria-expanded={open}>
        <Label>
          <NotificationOutlined />
          {MESSAGES.NEWS_FEED.SEGMENT_LABEL}
        </Label>
        <ValueRow>
          <span>
            <Count>{news.total}</Count> {windowLabel}
          </span>
          {newCount > 0 && (
            <NewBadge>
              <LegendCircle color={compassLightGreen} size={7} />
              {newCount} new
            </NewBadge>
          )}
          {news.total > 0 && (
            <NewsDayBars
              days={news.daily_counts}
              height={TINY_BAR_HEIGHT}
              barWidth={TINY_BAR_WIDTH}
              newSince={seenAt}
            />
          )}
          <Chevron />
        </ValueRow>
      </SegmentButton>
    </Popover>
  );
};
